

















































































































































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import {
  Contact,
  ContactsApi,
  ContactPagedCollectionApiResponse,
  PerformoSettings,
  QueryContactsRequest,
  ColumnDisplayOption,
  Property
} from 'performo-api';
import { mapGetters } from 'vuex';
import { SortChangedEvent } from '@/models/SortChangedEvent';
import { handleApiError } from '@/ApiHelpers';

@Component({
  components: {},
  computed: mapGetters({
    settings: 'settings',
    currentProperty: 'currentProperty',
    api: 'contactsApi'
  })
})
export default class ContactPicker extends Vue {
  @Prop({ required: true })
  private value!: number;

  @Prop({ required: false, default: false })
  private required?: boolean;

  @Prop({ required: true, type: String })
  private propertyId!: string;

  @Prop({ required: false, default: 'Contact' })
  private labelOverride?: string;

  private settings!: PerformoSettings;
  private currentProperty!: Property;
  private api!: ContactsApi;

  private isLoading = false;
  private showModal = false;

  private autoCompleteQuery = '';
  private autoCompleteContacts!: ContactPagedCollectionApiResponse;

  private query!: QueryContactsRequest;
  private pagedContacts!: ContactPagedCollectionApiResponse;

  private internalTake = 25;

  get contacts(): ContactPagedCollectionApiResponse | undefined {
    if (this.pagedContacts) {
      return this.pagedContacts || [];
    }
    return undefined;
  }

  get contactDisplayOptions(): ColumnDisplayOption[] {
    var cols = [
      { displayName: 'Last Name', columnName: 'lastName', width: 'auto' },
      { displayName: 'First Name', columnName: 'firstName', width: 'auto' },
      {
        displayName: this.currentProperty.ordererFieldLabel2,
        columnName:
          this.settings.ordererTypeDefault === 'Tenants'
            ? 'tenantName'
            : 'departmentName',
        width: 'auto'
      },
      {
        displayName: this.currentProperty.location1Label || 'Building',
        columnName: 'location1',
        width: 'auto'
      },
      {
        displayName: this.currentProperty.location2Label || 'Room',
        columnName: 'location2',
        width: '80px'
      },
      { displayName: 'Selected', columnName: 'selected', width: '80px' }
    ];

    if (this.settings.enableCampuses) {
      // when campus mode is enabled, ensure this column is displayed
      cols.splice(3, 0, {
        displayName: this.settings.campusLabelSingular,
        columnName: 'campus',
        width: 'auto'
      });
    }

    return cols;
  }

  private autoCompleteSelected(item: Contact) {
    if (item) {
      this.$emit('input', item.contactId);
      this.$emit('contactChanged', item);
    } else {
      this.$emit('input', null);
      this.$emit('contactChanged', {});
    }
  }

  private filterContact(item: Contact, queryText: string) {
    const searchFirstName = item.firstName ? item.firstName.toLowerCase() : '';
    const searchLastName = item.lastName ? item.lastName.toLowerCase() : '';
    return (
      searchFirstName.indexOf(queryText.toLowerCase()) > -1 ||
      searchLastName.indexOf(queryText.toLowerCase()) > -1
    );
  }

  data() {
    // this lets the undefined query! refs become reactive
    // https://class-component.vuejs.org/guide/class-component.html#data
    return {
      query: undefined,
      pagedContacts: undefined,
      autoCompleteQuery: undefined,
      autoCompleteContacts: undefined,
      isLoading: false
    };
  }

  @Watch('autoCompleteQuery', { immediate: true })
  private async autoCompleteQueryChanged() {
    this.isLoading = true;
    const query = {
      propertyId: this.propertyId,
      skip: 0,
      take: 10000,
      sortColumn: 'lastName',
      sortDirection: 'ascend'
    };
    try {
      this.autoCompleteContacts = await this.api.queryContacts(query);
    } catch (error) {
      handleApiError(error);
    }
    this.isLoading = false;
  }

  private itemClicked(item: Contact) {
    if (item) {
      this.$emit('input', item.contactId);
      this.$emit('contactChanged', item);
      this.showModal = false;
    } else {
      this.$emit('input', null);
      this.$emit('contactChanged', {});
    }
  }

  private async sortChangedHandler(sort: SortChangedEvent) {
    this.query.sortColumn = sort.column;
    this.query.sortDirection = sort.direction === 1 ? 'ascend' : 'descend';
    this.query.skip = 0;
    await this.fetchContacts();
  }

  private async paginationChangedHandler(pageNumber: number) {
    this.query.skip = (this.query.take || 25) * (pageNumber - 1) + 1;
    await this.fetchContacts();
  }

  @Watch('query', { deep: true })
  private async queryChanged(val: QueryContactsRequest) {
    if (val) {
      this.query.skip = 0;
      await this.fetchContacts();
    }
  }

  private async fetchContacts() {
    const query = {
      ...this.query,
      propertyId: this.propertyId,
      skip: this.query.skip ? this.query.skip : 0,
      take: this.query.take ? this.query.take : this.internalTake,
      sortColumn: this.query.sortColumn !== '' ? this.query.sortColumn : '',
      sortDirection:
        this.query.sortDirection !== '' ? this.query.sortDirection : 'ascend'
    };
    try {
      this.pagedContacts = await this.api.queryContacts(query);
    } catch (error) {
      handleApiError(error);
    }
  }

  @Watch('propertyId')
  private async propertyIdChanged() {
    this.autoCompleteQueryChanged();
    this.fetchContacts();
  }

  private async mounted() {
    await this.autoCompleteQueryChanged();

    this.query = {
      lastName: '',
      sortColumn: 'lastName',
      sortDirection: 'ascend'
    };
    await this.fetchContacts();
  }
}
